import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { FirLocalSysChartAcctMapService } from './fir-local-sys-chart-acct-map.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-fir-local-sys-chart-acct-map',
    templateUrl: './fir-local-sys-chart-acct-map.component.html',
    styleUrls: ['./fir-local-sys-chart-acct-map.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FirLocalSysChartAcctMapComponent implements OnInit, OnDestroy {

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly contextSubs = new Subscription();
  private contextEpoch = 0;

  // V9: Flags to prevent duplicate initialization
  private isInitialized = false;
  private isLoading = false;
  private unsubscribe$ = new Subject<void>();
  /** Bumped on each `init()` / destroy so slower HTTP callbacks cannot commit after a newer load. */
  private loadEpoch = 0;
  /** Serial for `fetchSystemAcctFormData` only (system acct changes can overlap). */
  private formFetchEpoch = 0;

  // Standard component properties
  modalTitle = 'Local-System Acct Mapping';
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = true;

  // Loading states
  loadingFirSysAcct = true;
  loadingFirLookup = false;
  loadingFirTaxSensLookup = true;

  // Form data
  firLocalChartAcct_data: any = {
    acct_type: '',
    local_chart: '',
    localaccts: '',
    system_acct: '',
    local_acct_tax_sensitive: ''
  };

  // System Account dropdown
  fir_sys_acct_selected = 'Please select System Account';
  fir_sys_acc_dropdown_data: any[] = [];
  fir_sys_acc_dropdown_data_TEMP: any[] = [];
  fir_sys_acc: any = {};

  // Lookup panel
  firLookUpMapping = false;
  showFirDetails: any = { text: 'lookup' };
  firSystemAcctLookupTemp: any[] = [];
  firLookupForm: string[] = [];
  firLookupSchedule: string[] = [];
  firLookupLine: string[] = [];
  firLookupColumn: string[] = [];
  firLookupSysMapAcct: string[] = [];
  firLookupcheckdata: any[] = [];

  CHECK_FIR_FORM = '';
  CHECK_FIR_SCHEDULE = '';
  CHECK_FIR_LINE = '';
  CHECK_FIR_LINE_DESC = '';
  CHECK_FIR_COL = '';
  CHECK_FIR_SYS_MAP_ACCT = '';

  // Tax Sensitive panel
  firTaxSensLookup = true;
  firTaxsensitivelookUpArrow = true;
  fir_tax_sensitive_data: any[] = [];
  fir_tax_sensitive_data_temp: any[] = [];
  firPostingLocalAcctList: any[] = [];
  firOffsetLocalAcctList: any[] = [];
  form5471SchdH: any[] = [];
  form8865SchdM1: any[] = [];
  firIRSFormData: any[] = [];
  firEPTypeList = [
    { EP_TYPE_CODE: 'AB', EP_TYPE_DESC: 'Change In Balance' },
    { EP_TYPE_CODE: 'AR', EP_TYPE_DESC: 'Reverse PL' },
    { EP_TYPE_CODE: 'AY', EP_TYPE_DESC: 'BS acct - CY Activity' }
  ];
  flag_value = [
    { flag: 'Yes', value: 'Y' },
    { flag: 'No', value: 'N' }
  ];
  yesNoOptions = [
    { flag: 'Yes', value: 'Y' },
    { flag: 'No', value: 'N' }
  ];
  permTempFlag = [
    { flag: 'Permanent', value: 'PM' },
    { flag: 'Temporary', value: 'TN' }
  ];

  compareByValue = (a: { value?: string } | null, b: { value?: string } | null): boolean =>
    !!a && !!b && a.value === b.value;

  compareByEpTypeCode = (a: { EP_TYPE_CODE?: string } | null, b: { EP_TYPE_CODE?: string } | null): boolean =>
    !!a && !!b && a.EP_TYPE_CODE === b.EP_TYPE_CODE;

  compareByPostingLocalAcctKey = (a: { POSTING_LOCAL_ACCT_KEY?: string } | null, b: { POSTING_LOCAL_ACCT_KEY?: string } | null): boolean =>
    !!a && !!b && a.POSTING_LOCAL_ACCT_KEY === b.POSTING_LOCAL_ACCT_KEY;

  compareByOffsetLocalAcctKey = (a: { OFFSET_LOCAL_ACCT_KEY?: string } | null, b: { OFFSET_LOCAL_ACCT_KEY?: string } | null): boolean =>
    !!a && !!b && a.OFFSET_LOCAL_ACCT_KEY === b.OFFSET_LOCAL_ACCT_KEY;

  compareByLine5471 = (a: { LINE_NO?: string } | null, b: { LINE_NO?: string } | null): boolean =>
    !!a && !!b && a.LINE_NO === b.LINE_NO;

  compareByLine8865 = (a: { LINE?: string } | null, b: { LINE?: string } | null): boolean =>
    !!a && !!b && a.LINE === b.LINE;

  // Sorting for IRS Form table
  orderByField = 'FORM';
  reverseSort: boolean | string = false;
  sort_key: any = { '1': 0, '2': 0, '3': 0, '4': 0, '5': 0 };

  // Local accounts handling
  firLocalAccountMultipleSelected: string[] = [];
  firlocalAccountKeys: any[] = [];
  sysAcctHide = false;
  selectedFirSysAcc = '';

  // Base configuration
  baseURLs: GtwElementsBaseUrlsModel = {
    admin: '',
    api: '',
    custom: '',
    efile: '',
    pdfEngine: '',
    bulkPdfEngine: ''
  };

  globalService: any;
  GENERAL_CONFIG: any;
  rowData: any = {};
  colData: any = {};
  gridData: any[] = [];
  filterParams: any = {};

  @Input('col-data')
  set _colData(colData: string) {
    try {
      if (typeof colData === 'string' && (colData.includes('{{') || colData === '')) {
        return;
      }
      this.colData = safeJsonInput<any>(colData, null, 'fir-local-sys-chart-acct-map.parse1');
      if (this.colData && this.colData.call_back === 'foreignLocalAcctTaxSens') {
        this.sysAcctHide = true;
      }
    } catch (e) {
      console.log("Col data input not yet stable.");
    }
  }

  @Input('grid-data')
  set _gridData(gridData: string) {
    try {
      if (typeof gridData === 'string' && (gridData.includes('{{') || gridData === '')) {
        return;
      }
      this.gridData = safeJsonInput<any>(gridData, null, 'fir-local-sys-chart-acct-map.parse2');
      this.tryInit();
    } catch (e) {
      console.log("Grid data input not yet stable.");
    }
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private _snackBar: MatSnackBar,
    private firLocalSysService: FirLocalSysChartAcctMapService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.resetAndTryInit();
    this.contextSubs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.resetAndTryInit();
      })
    );
    this.contextSubs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.resetAndTryInit();
      })
    );
  }

  ngOnDestroy(): void {
    this.contextEpoch++;
    this.loadEpoch++;
    this.formFetchEpoch++;
    this.contextSubs.unsubscribe();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    let fp = normalizeFilterParams(activeScreen);
    if (!Object.keys(fp).length) {
      fp = extractObject(hydrated);
    }
    this.filterParams = fp;
    const mergedGp = { ...extractObject(shell.globalParams), ...extractObject(hydrated) };
    this.globalService = {
      ...shell,
      globalParams: mergedGp,
      filterParams: fp,
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
      getScenario: (ty: any) => this.gtwGlobal.getScenario(ty),
      getEIN: () => this.gtwGlobal.getEIN(),
      getTAXYEAR: () => this.gtwGlobal.getTAXYEAR(),
      getChartType: () => this.gtwGlobal.getChartType(),
      inputs: this.gtwGlobal.inputs,
      sso_id: this.gtwApp?.USER_SETTINGS?.user?.sso_id ?? ''
    };
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url || gc.api || '' };
    this.baseURLs = gc as GtwElementsBaseUrlsModel;
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow)
      ? extractObject(rawRow[0]?.data ?? rawRow[0])
      : extractObject(rawRow);
  }

  private resetAndTryInit(): void {
    this.contextEpoch++;
    this.loadEpoch++;
    this.formFetchEpoch++;
    this.isInitialized = false;
    this.isLoading = false;
    this.applyWorkspaceContext();
    this.tryInit();
    this.cdr.markForCheck();
  }

  // V9: Gate function to prevent duplicate initialization
  private tryInit(): void {
    if (this.isInitialized || this.isLoading) {
      return;
    }

    if (!this.globalService || !this.GENERAL_CONFIG) {
      return;
    }

    // Check if we have either rowData or gridData
    if ((!this.rowData || Object.keys(this.rowData).length === 0) &&
        (!this.gridData || this.gridData.length === 0)) {
      return;
    }

    this.isInitialized = true;
    this.init();
  }

  init(): void {
    const epoch = ++this.loadEpoch;
    try {
      this.setupFormData();
      this.fetchFirLocalPostingAcct(epoch);
      this.getFirSystemAcctdropdown(epoch);
      this.fetchFirSystemAcctLookup(epoch);
      this.getF5471SchdHData(epoch);
      this.getF8865SchdM1Data(epoch);
    } catch (e) {
      console.error('Error in component init:', e);
      this.showFormLoader = false;
      this.cdr.detectChanges();
    }
  }

  // Fetch Local Posting Accounts
  private fetchFirLocalPostingAcct(epoch: number): void {
    const params = {
      action_code: 'xzxjuh',
      local_acct_key: this.firlocalAccountKeys.join(','),
      tax_year: this.rowData?.TAX_YEAR || this.gridData?.[0]?.data?.TAX_YEAR || this.globalService?.globalParams?.tax_year,
      scenario: this.globalService?.globalParams?.scenario,
      jcd_key: this.globalService?.globalParams?.jcd_key
    };

    this.firLocalSysService.getLocalPostingAcct(
      this.GENERAL_CONFIG.base_url || this.baseURLs.api,
      params,
      this.globalService
    ).pipe(takeUntil(this.unsubscribe$)).subscribe(
      (data: any) => {
        if (epoch !== this.loadEpoch) {
          return;
        }
        if (data.callSuccess === '1' && data.jsonObject) {
          for (const item of data.jsonObject) {
            if (item.ACCT_FLAG === 'INCOME_STATEMENT') {
              this.firPostingLocalAcctList.push(item);
            } else {
              this.firOffsetLocalAcctList.push(item);
            }
          }
          // Sort the lists
          this.firPostingLocalAcctList.sort((a, b) => 
            (a.POSTING_LOCAL_ACCT || '').localeCompare(b.POSTING_LOCAL_ACCT || ''));
          this.firOffsetLocalAcctList.sort((a, b) => 
            (a.OFFSET_LOCAL_ACCT || '').localeCompare(b.OFFSET_LOCAL_ACCT || ''));
        }
        // Now fetch tax sensitive data
        this.fetchFirTaxSensitiveData(epoch);
        this.cdr.detectChanges();
      },
      (error: any) => {
        if (epoch !== this.loadEpoch) {
          return;
        }
        console.error('Error loading posting accounts:', error);
        this.fetchFirTaxSensitiveData(epoch);
        this.cdr.detectChanges();
      }
    );
  }

  private setupFormData(): void {
    if (this.rowData && Object.keys(this.rowData).length > 0) {
      this.firLocalChartAcct_data = {
        acct_type: this.rowData.ACCT_TYPE || '',
        local_chart: this.rowData.CHART_DISPLAY || '',
        localaccts: this.rowData.LOCAL_ACCT_DISPLAY || '',
        system_acct: this.rowData.SYSTEM_ACCT || '',
        local_acct_tax_sensitive: this.rowData.SENSITIVE_ACCT || ''
      };
      this.selectedFirSysAcc = this.rowData.SYSTEM_ACCT;
      this.firLocalAccountMultipleSelected = [this.rowData.LOCAL_ACCT_DISPLAY];
      this.firlocalAccountKeys = [this.rowData.LOCAL_ACCT_KEY];
    } else if (this.gridData && this.gridData.length > 0) {
      // Multiple selection from grid
      this.buildLocalAccountsFromGrid();
      const firstItem = this.gridData[0].data || this.gridData[0];
      this.firLocalChartAcct_data = {
        acct_type: firstItem.ACCT_TYPE || '',
        local_chart: firstItem.CHART_DISPLAY || '',
        localaccts: this.firLocalAccountMultipleSelected.join(', '),
        system_acct: firstItem.SYSTEM_ACCT || '',
        local_acct_tax_sensitive: firstItem.SENSITIVE_ACCT || ''
      };
      this.selectedFirSysAcc = firstItem.SYSTEM_ACCT;
    }
    this.showFormLoader = false;
    this.cdr.detectChanges();
  }

  private buildLocalAccountsFromGrid(): void {
    this.firLocalAccountMultipleSelected = [];
    this.firlocalAccountKeys = [];
    
    for (const item of this.gridData) {
      const data = item.data || item;
      if (data.LOCAL_ACCT_DISPLAY) {
        this.firLocalAccountMultipleSelected.push(data.LOCAL_ACCT_DISPLAY);
      }
      if (data.LOCAL_ACCT_KEY) {
        this.firlocalAccountKeys.push(data.LOCAL_ACCT_KEY);
      }
    }
    
    // Remove duplicates
    this.firLocalAccountMultipleSelected = [...new Set(this.firLocalAccountMultipleSelected)];
    this.firlocalAccountKeys = [...new Set(this.firlocalAccountKeys)];
  }

  // Get System Account Dropdown
  private getFirSystemAcctdropdown(epoch: number): void {
    const params = {
      action_code: 'pfwnw7',
      tax_year: this.rowData?.TAX_YEAR || this.gridData?.[0]?.data?.TAX_YEAR || this.globalService?.globalParams?.tax_year,
      scenario: this.rowData?.SCENARIO || this.globalService?.globalParams?.scenario,
      jcd_key: this.rowData?.JCD_KEY || this.globalService?.globalParams?.jcd_key
    };

    this.firLocalSysService.getSystemAcctDropdown(
      this.GENERAL_CONFIG.base_url || this.baseURLs.api,
      params,
      this.globalService
    ).pipe(takeUntil(this.unsubscribe$)).subscribe(
      (data: any) => {
        if (epoch !== this.loadEpoch) {
          return;
        }
        if (data.callSuccess === '1' && data.jsonObject) {
          this.fir_sys_acc_dropdown_data_TEMP = data.jsonObject;
          this.fir_sys_acc_dropdown_data = data.jsonObject.map((item: any) => item.SYS_DROPDOWN);

          // Find current system account
          const index = this.fir_sys_acc_dropdown_data_TEMP.findIndex(
            (item: any) => item.SYS_ACCT === this.selectedFirSysAcc
          );

          if (index !== -1) {
            this.fir_sys_acc.SYS_ACCT = this.fir_sys_acc_dropdown_data_TEMP[index];
            this.fir_sys_acct_selected = this.fir_sys_acc_dropdown_data_TEMP[index].SYS_DROPDOWN || 'Please select System Account';
            // Load IRS Form data for the initial system account
            this.fetchSystemAcctFormData();
          }
        }
        this.loadingFirSysAcct = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        if (epoch !== this.loadEpoch) {
          return;
        }
        this.loadingFirSysAcct = false;
        console.error('Error loading system account dropdown:', error);
        this.cdr.detectChanges();
      }
    );
  }

  // Fetch System Account Lookup
  private fetchFirSystemAcctLookup(epoch: number): void {
    const params = {
      action_code: 'oz9yxt',
      tax_year: this.rowData?.TAX_YEAR || this.gridData?.[0]?.data?.TAX_YEAR || this.globalService?.globalParams?.tax_year,
      scenario: this.rowData?.SCENARIO || this.globalService?.globalParams?.scenario,
      jcd_key: this.rowData?.JCD_KEY || this.globalService?.globalParams?.jcd_key
    };

    this.firLocalSysService.getSystemAcctLookup(
      this.GENERAL_CONFIG.base_url || this.baseURLs.api,
      params,
      this.globalService
    ).pipe(takeUntil(this.unsubscribe$)).subscribe(
      (data: any) => {
        if (epoch !== this.loadEpoch) {
          return;
        }
        if (data.callSuccess === '1' && data.jsonObject) {
          this.firSystemAcctLookupTemp = data.jsonObject;
          this.firLookupcheckdata = data.jsonObject;
          this.firLookupForm = [...new Set(data.jsonObject.map((item: any) => item.FORM))] as string[];
        }
        this.loadingFirLookup = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        if (epoch !== this.loadEpoch) {
          return;
        }
        this.loadingFirLookup = false;
        console.error('Error loading lookup data:', error);
        this.cdr.detectChanges();
      }
    );
  }

  // Fetch Tax Sensitive Data - using correct action code from AngularJS (lxmsjj)
  // Matching original AngularJS fetchFirTaxSensitiveData exactly
  private fetchFirTaxSensitiveData(epoch: number): void {
    const localAcctKeys = this.firlocalAccountKeys.join(',');
    const params = {
      action_code: 'lxmsjj',
      sys_acct_key: null,
      local_acct_key: localAcctKeys,
      tax_year: this.rowData?.TAX_YEAR || this.gridData?.[0]?.data?.TAX_YEAR || this.globalService?.globalParams?.tax_year,
      scenario: this.globalService?.globalParams?.scenario,
      jcd_key: this.globalService?.globalParams?.jcd_key
    };

    this.firLocalSysService.getTaxSensitiveData(
      this.GENERAL_CONFIG.base_url || this.baseURLs.api,
      params,
      this.globalService
    ).pipe(takeUntil(this.unsubscribe$)).subscribe(
      (data: any) => {
        if (epoch !== this.loadEpoch) {
          return;
        }
        if (data.callSuccess === '1' && data.jsonObject && data.jsonObject.length > 0) {
          this.fir_tax_sensitive_data_temp = data.jsonObject;
          this.fir_tax_sensitive_data = this.buildFirTaxSensitiveRows(data.jsonObject);
        }
        this.loadingFirTaxSensLookup = false;
        this.firTaxsensitivelookUpArrow = true;
        this.cdr.detectChanges();
      },
      (error: any) => {
        if (epoch !== this.loadEpoch) {
          return;
        }
        this.loadingFirTaxSensLookup = false;
        this.firTaxSensLookup = false;
        console.error('Error loading tax sensitive data:', error);
        this.cdr.detectChanges();
      }
    );
  }

  private buildFirTaxSensitiveRows(sourceItems: any[]): any[] {
    return sourceItems.map((item: any) => this.mapFirTaxSensitiveItem(item));
  }

  private mapFirTaxSensitiveItem(item: any): any {
    let tempLocalAcct = '';
    let tempLocalChartKey = '';

    if (!this.rowData || Object.keys(this.rowData).length === 0) {
      for (const gridItem of this.gridData) {
        const gData = gridItem.data || gridItem;
        if (gData.LOCAL_ACCT_KEY === item.TRIGGER_LOCAL_ACCT_KEY) {
          tempLocalAcct = gData.LOCAL_ACCT || gData.LOCAL_ACCT_DISPLAY || '';
          tempLocalChartKey = gData.LOCAL_CHART_KEY || '';
          break;
        }
      }
    } else {
      tempLocalAcct = this.rowData.LOCAL_ACCT || this.rowData.LOCAL_ACCT_DISPLAY || '';
      tempLocalChartKey = this.rowData.LOCAL_CHART_KEY || '';
    }

    return {
      TRIGGER_LOCAL_ACCT_KEY: item.TRIGGER_LOCAL_ACCT_KEY,
      TRIGGER_LOCAL_ACCT: tempLocalAcct || item.TRIGGER_LOCAL_ACCT_KEY,
      LOCAL_ACCT_TAX_SENSITIVE: item.TAX_SENSITIVE === 'Y'
        ? { flag: 'Yes', value: 'Y' }
        : { flag: 'No', value: 'N' },
      EP_AUTO_KEY: item.EP_AUTO_KEY ?? null,
      EP_DESC: item.EP_DESC ?? null,
      PERM_TEMP_FLAG: item.PERM_TEMP_FLAG === 'PM'
        ? { flag: 'Permanent', value: 'PM' }
        : item.PERM_TEMP_FLAG === 'TN'
          ? { flag: 'Temporary', value: 'TN' }
          : item.PERM_TEMP_FLAG,
      EP_TYPE_CODE: item.EP_TYPE_CODE ?? '',
      EP_TYPE_DESC: item.EP_TYPE_DESC ?? '',
      EP_TYPE_DETAILS: {
        EP_TYPE_CODE: item.EP_TYPE_CODE ?? '',
        EP_TYPE_DESC: item.EP_TYPE_DESC ?? ''
      },
      MULTIPLIER: item.MULTIPLIER,
      DEFAULT_METHOD: item.DEFAULT_METHOD === 'Y'
        ? { flag: 'Yes', value: 'Y' }
        : item.DEFAULT_METHOD === 'N'
          ? { flag: 'No', value: 'N' }
          : item.DEFAULT_METHOD,
      PERMISSIBLE: item.PERMISSIBLE === 'Y'
        ? { flag: 'Yes', value: 'Y' }
        : item.PERMISSIBLE === 'N'
          ? { flag: 'No', value: 'N' }
          : item.PERMISSIBLE,
      AMT_SIGN: item.AMT_SIGN ?? null,
      LINE_5471: item.LINE_5471 ?? '',
      LINE_5471_DETAILS: {
        IRS_FORM_NO: 'F5471',
        SCHEDULE: 'H',
        LINE_NO: item.LINE_5471 ?? '',
        DESCRIPTION: this.getLineDesc('F5471', item.LINE_5471)
      },
      LINE_8865: item.LINE_8865 ?? '',
      LINE_8865_DETAILS: {
        IRS_FORM_NO: 'F8865',
        SCHEDULE: 'K',
        LINE: item.LINE_8865 ?? '',
        DESCRIPTION: this.getLineDesc('F8865', item.LINE_8865)
      },
      POSTING_LOCAL_ACCT_KEY: item.POSTING_LOCAL_ACCT_KEY ?? null,
      POSTING_LOCAL_ACCT_DETAILS: item.POSTING_LOCAL_ACCT_KEY ? {
        CHART_KEY: tempLocalChartKey,
        POSTING_LOCAL_ACCT: item.POSTING_LOCAL_ACCT,
        POSTING_LOCAL_ACCT_KEY: item.POSTING_LOCAL_ACCT_KEY
      } : null,
      OFFSET_LOCAL_ACCT_KEY: item.OFFSET_LOCAL_ACCT_KEY ?? null,
      OFFSET_LOCAL_ACCT_DETAILS: item.OFFSET_LOCAL_ACCT_KEY ? {
        CHART_KEY: tempLocalChartKey,
        OFFSET_LOCAL_ACCT: item.OFFSET_LOCAL_ACCT,
        OFFSET_LOCAL_ACCT_KEY: item.OFFSET_LOCAL_ACCT_KEY
      } : null,
      isEditable: false
    };
  }

  private syncFirTaxSensitiveRowForEdit(row: any): void {
    const taxSensValue = row.LOCAL_ACCT_TAX_SENSITIVE?.value ?? row.TAX_SENSITIVE;
    if (taxSensValue) {
      const matchedTaxSens = this.flag_value.find((f) => f.value === taxSensValue);
      if (matchedTaxSens) {
        row.LOCAL_ACCT_TAX_SENSITIVE = matchedTaxSens;
      }
    }

    const postingKey = row.POSTING_LOCAL_ACCT_KEY ?? row.POSTING_LOCAL_ACCT_DETAILS?.POSTING_LOCAL_ACCT_KEY;
    if (postingKey) {
      const matchedPosting = this.firPostingLocalAcctList.find(
        (acct) => acct.POSTING_LOCAL_ACCT_KEY === postingKey
      );
      if (matchedPosting) {
        row.POSTING_LOCAL_ACCT_DETAILS = matchedPosting;
      }
    }

    const offsetKey = row.OFFSET_LOCAL_ACCT_KEY ?? row.OFFSET_LOCAL_ACCT_DETAILS?.OFFSET_LOCAL_ACCT_KEY;
    if (offsetKey) {
      const matchedOffset = this.firOffsetLocalAcctList.find(
        (acct) => acct.OFFSET_LOCAL_ACCT_KEY === offsetKey
      );
      if (matchedOffset) {
        row.OFFSET_LOCAL_ACCT_DETAILS = matchedOffset;
      }
    }

    const epTypeCode = row.EP_TYPE_DETAILS?.EP_TYPE_CODE ?? row.EP_TYPE_CODE;
    if (epTypeCode) {
      const matchedEpType = this.firEPTypeList.find((ep) => ep.EP_TYPE_CODE === epTypeCode);
      if (matchedEpType) {
        row.EP_TYPE_DETAILS = matchedEpType;
      }
    }

    const line5471 = row.LINE_5471_DETAILS?.LINE_NO ?? row.LINE_5471;
    if (line5471) {
      const matchedLine5471 = this.form5471SchdH.find((line) => line.LINE_NO === line5471);
      if (matchedLine5471) {
        row.LINE_5471_DETAILS = matchedLine5471;
      }
    }

    const line8865 = row.LINE_8865_DETAILS?.LINE ?? row.LINE_8865;
    if (line8865) {
      const matchedLine8865 = this.form8865SchdM1.find((line) => line.LINE === line8865);
      if (matchedLine8865) {
        row.LINE_8865_DETAILS = matchedLine8865;
      }
    }

    const permValue = row.PERM_TEMP_FLAG?.value ?? row.PERM_TEMP_FLAG;
    if (permValue === 'PM' || permValue === 'TN') {
      const matchedPerm = this.permTempFlag.find((pt) => pt.value === permValue);
      if (matchedPerm) {
        row.PERM_TEMP_FLAG = matchedPerm;
      }
    }

    const defaultMethodValue = row.DEFAULT_METHOD?.value ?? row.DEFAULT_METHOD;
    if (defaultMethodValue === 'Y' || defaultMethodValue === 'N') {
      const matchedDefault = this.yesNoOptions.find((opt) => opt.value === defaultMethodValue);
      if (matchedDefault) {
        row.DEFAULT_METHOD = matchedDefault;
      }
    }

    const permissibleValue = row.PERMISSIBLE?.value ?? row.PERMISSIBLE;
    if (permissibleValue === 'Y' || permissibleValue === 'N') {
      const matchedPermissible = this.yesNoOptions.find((opt) => opt.value === permissibleValue);
      if (matchedPermissible) {
        row.PERMISSIBLE = matchedPermissible;
      }
    }
  }

  // Helper function to get line description - matching original AngularJS vm.getLineDesc
  private getLineDesc(form: string, line: string | null | undefined): string {
    if (!line) return '';
    
    if (form === 'F5471') {
      const found = this.form5471SchdH.find((item: any) => item.LINE_NO === line);
      return found?.DESCRIPTION || '';
    }
    if (form === 'F8865') {
      const found = this.form8865SchdM1.find((item: any) => item.LINE === line);
      return found?.DESCRIPTION || '';
    }
    return '';
  }

  // Fetch System Account Form Data (IRS Form table at bottom) - action_code: 4fmxwf
  // Uses GET request with all filter params like in original AngularJS
  private fetchSystemAcctFormData(): void {
    if (!this.fir_sys_acc?.SYS_ACCT?.SYS_ACCT_KEY) {
      return;
    }

    const formEpoch = ++this.formFetchEpoch;

    // Build params matching original AngularJS - all filterParams + globalParams
    const params: any = {
      action_code: '4fmxwf',
      tax_year: this.rowData?.TAX_YEAR || this.gridData?.[0]?.data?.TAX_YEAR || this.globalService?.globalParams?.tax_year,
      local_acct: this.rowData?.LOCAL_ACCT_KEY || this.gridData?.[0]?.data?.LOCAL_ACCT_KEY,
      sys_acct: this.fir_sys_acc.SYS_ACCT.SYS_ACCT_KEY,
      scenario: this.globalService?.globalParams?.scenario,
      jcd_key: this.globalService?.globalParams?.jcd_key
    };

    // Merge with filterParams (includes activity_key, chart_filter, screen_key etc.)
    const allParams = { ...this.filterParams, ...this.globalService?.globalParams, ...params };

    this.firLocalSysService.getSystemAcctFormData(
      this.GENERAL_CONFIG.base_url || this.baseURLs.api,
      allParams,
      this.globalService
    ).pipe(takeUntil(this.unsubscribe$)).subscribe(
      (data: any) => {
        if (formEpoch !== this.formFetchEpoch) {
          return;
        }
        if (data.callSuccess === '1' && data.jsonObject) {
          this.firIRSFormData = data.jsonObject;
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        if (formEpoch !== this.formFetchEpoch) {
          return;
        }
        console.error('Error loading IRS form data:', error);
        this.cdr.detectChanges();
      }
    );
  }

  // Fetch 5471 Schedule H Data - action_code: 6mnbxg from AngularJS
  private getF5471SchdHData(epoch: number): void {
    const params = {
      action_code: '6mnbxg',
      tax_year: this.rowData?.TAX_YEAR || this.gridData?.[0]?.data?.TAX_YEAR || this.globalService?.globalParams?.tax_year,
      scenario: this.globalService?.globalParams?.scenario,
      jcd_key: this.globalService?.globalParams?.jcd_key
    };

    this.firLocalSysService.getF5471SchdHData(
      this.GENERAL_CONFIG.base_url || this.baseURLs.api,
      params,
      this.globalService
    ).pipe(takeUntil(this.unsubscribe$)).subscribe(
      (data: any) => {
        if (epoch !== this.loadEpoch) {
          return;
        }
        if (data.callSuccess === '1' && data.jsonObject) {
          this.form5471SchdH = data.jsonObject;
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        if (epoch !== this.loadEpoch) {
          return;
        }
        console.error('Error loading 5471 Schedule H data:', error);
      }
    );
  }

  // Fetch 8865 Schedule M1 Data - action_code: r7pupz from AngularJS
  private getF8865SchdM1Data(epoch: number): void {
    const params = {
      action_code: 'r7pupz',
      tax_year: this.rowData?.TAX_YEAR || this.gridData?.[0]?.data?.TAX_YEAR || this.globalService?.globalParams?.tax_year,
      scenario: this.globalService?.globalParams?.scenario,
      jcd_key: this.globalService?.globalParams?.jcd_key
    };

    this.firLocalSysService.getF8865SchdM1Data(
      this.GENERAL_CONFIG.base_url || this.baseURLs.api,
      params,
      this.globalService
    ).pipe(takeUntil(this.unsubscribe$)).subscribe(
      (data: any) => {
        if (epoch !== this.loadEpoch) {
          return;
        }
        if (data.callSuccess === '1' && data.jsonObject) {
          this.form8865SchdM1 = data.jsonObject;
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        if (epoch !== this.loadEpoch) {
          return;
        }
        console.error('Error loading 8865 Schedule M1 data:', error);
      }
    );
  }

  // System Account change handler
  onSystemAcctChange(): void {
    // Find selected system account from dropdown
    const index = this.fir_sys_acc_dropdown_data_TEMP.findIndex(
      (item: any) => item.SYS_DROPDOWN === this.fir_sys_acct_selected
    );

    if (index !== -1) {
      this.fir_sys_acc.SYS_ACCT = this.fir_sys_acc_dropdown_data_TEMP[index];
      this.firLocalChartAcct_data.system_acct = this.fir_sys_acc_dropdown_data_TEMP[index].SYS_ACCT;
      
      // Check if tax sensitive and show warning
      if (this.fir_sys_acc.SYS_ACCT.TAX_SENSITIVE === 'Y') {
        this._snackBar.open('Selected system account is tax sensitive', 'Close', {
          duration: 4000,
          panelClass: ['warning-snackbar']
        });
      }
      
      // Fetch IRS form data for new system account
      this.fetchSystemAcctFormData();
    }
    this.cdr.detectChanges();
  }

  // Toggle lookup panel
  firToggleLookup(): void {
    this.CHECK_FIR_FORM = '';
    this.CHECK_FIR_SCHEDULE = '';
    this.CHECK_FIR_LINE = '';
    this.CHECK_FIR_LINE_DESC = '';
    this.CHECK_FIR_COL = '';
    this.CHECK_FIR_SYS_MAP_ACCT = '';
    this.firLookUpMapping = !this.firLookUpMapping;
    this.showFirDetails.text = 'lookup';
    this.cdr.detectChanges();
  }

  // Toggle Tax Sensitive panel
  firTaxsensitivetoggleLookup(): void {
    this.firTaxsensitivelookUpArrow = !this.firTaxsensitivelookUpArrow;
    this.cdr.detectChanges();
  }

  // Cascade lookup selection
  getFirLookupNext(key: number, nextLookupKey: string, currentLookupValue: string): void {
    const lookupData = [...this.firLookupcheckdata];

    switch (key) {
      case 1:
        this.firLookupSchedule = this.filterLookupData(lookupData, 'FORM', currentLookupValue, 'SCHEDULE');
        break;
      case 2:
        const scheduleFiltered = lookupData.filter((obj: any) =>
          obj.FORM === this.CHECK_FIR_FORM && obj.SCHEDULE === currentLookupValue
        );
        this.firLookupLine = [...new Set(scheduleFiltered.map((obj: any) => obj.LINE))];
        break;
      case 3:
        const lineFiltered = lookupData.filter((obj: any) =>
          obj.FORM === this.CHECK_FIR_FORM &&
          obj.SCHEDULE === this.CHECK_FIR_SCHEDULE &&
          obj.LINE === currentLookupValue
        );
        this.firLookupColumn = [...new Set(lineFiltered.map((obj: any) => obj.COL))];
        break;
      case 4:
        const colFiltered = lookupData.filter((obj: any) =>
          obj.FORM === this.CHECK_FIR_FORM &&
          obj.SCHEDULE === this.CHECK_FIR_SCHEDULE &&
          obj.LINE === this.CHECK_FIR_LINE &&
          obj.COL === currentLookupValue
        );
        this.firLookupSysMapAcct = [...new Set(colFiltered.map((obj: any) => obj.SYS_ACCT))];
        break;
    }
    this.cdr.detectChanges();
  }

  private filterLookupData(data: any[], filterKey: string, filterValue: string, extractKey: string): string[] {
    const filtered = data.filter((obj: any) => obj[filterKey] === filterValue);
    return [...new Set(filtered.map((obj: any) => obj[extractKey]))];
  }

  // Tax Sensitive edit - matching original AngularJS vm.firFormEdit
  firFormEdit(item: any): void {
    this.syncFirTaxSensitiveRowForEdit(item);
    item.isEditable = true;
    this.cdr.detectChanges();
  }

  // Sort function for IRS Form table - matching original AngularJS vm.sortBy
  sortBy(key: string, currentValue: number): void {
    // Cycle through: 0 -> 1 (asc) -> 2 (desc) -> 0 (no sort)
    let newValue: number;
    switch (currentValue) {
      case 0:
        newValue = 1;
        this.reverseSort = false;
        break;
      case 1:
        newValue = 2;
        this.reverseSort = true;
        break;
      case 2:
      default:
        newValue = 0;
        this.reverseSort = '';
        break;
    }
    
    // Reset all keys and set the current one
    Object.keys(this.sort_key).forEach(k => this.sort_key[k] = 0);
    this.sort_key[key] = newValue;

    // Sort the data
    if (newValue !== 0 && this.firIRSFormData.length > 0) {
      const sortField = this.getSortField(key);
      this.firIRSFormData = [...this.firIRSFormData].sort((a, b) => {
        const valA = (a[sortField] || '').toString().toLowerCase();
        const valB = (b[sortField] || '').toString().toLowerCase();
        const comparison = valA.localeCompare(valB);
        return this.reverseSort === true ? -comparison : comparison;
      });
    }
    
    this.cdr.detectChanges();
  }

  private getSortField(key: string): string {
    const fieldMap: any = {
      '1': 'FORM',
      '2': 'SCHEDULE',
      '3': 'LINE',
      '4': 'LINE_DESC',
      '5': 'COL'
    };
    return fieldMap[key] || 'FORM';
  }

  // Apply lookup changes - matching original AngularJS applyFirChanges + getChartAcctTableData
  applyFirChanges(): void {
    if (!this.CHECK_FIR_SYS_MAP_ACCT) {
      this._snackBar.open('Please select a system account from lookup', 'Close', {
        duration: 4000,
        panelClass: ['warning-snackbar']
      });
      return;
    }

    // Set selected system account from lookup (like vm.fir_sys_acct_selected = vm.CHECK_FIR_SYS_MAP_ACCT)
    this.fir_sys_acct_selected = this.CHECK_FIR_SYS_MAP_ACCT;
    
    // Find the system account in dropdown (like getChartAcctTableData does)
    const index = this.fir_sys_acc_dropdown_data_TEMP.findIndex(
      (item: any) => item.SYS_DROPDOWN === this.fir_sys_acct_selected
    );

    if (index !== -1) {
      this.fir_sys_acc.SYS_ACCT = this.fir_sys_acc_dropdown_data_TEMP[index];
      this.firLocalChartAcct_data.system_acct = this.fir_sys_acc_dropdown_data_TEMP[index].SYS_ACCT;
      
      // Check if tax sensitive and show warning
      if (this.fir_sys_acc.SYS_ACCT.TAX_SENSITIVE === 'Y') {
        this._snackBar.open('Selected system account is tax sensitive', 'Close', {
          duration: 4000,
          panelClass: ['warning-snackbar']
        });
      }
    }

    // Fetch IRS Form data (like fetchSystemAcctFormData in AngularJS)
    this.fetchSystemAcctFormData();

    // Close lookup panel
    this.firLookUpMapping = false;
    this.cdr.detectChanges();
  }

  // Reset lookup
  resetFirLookup(): void {
    this.CHECK_FIR_FORM = '';
    this.CHECK_FIR_SCHEDULE = '';
    this.CHECK_FIR_LINE = '';
    this.CHECK_FIR_LINE_DESC = '';
    this.CHECK_FIR_COL = '';
    this.CHECK_FIR_SYS_MAP_ACCT = '';
    this.firLookupSchedule = [];
    this.firLookupLine = [];
    this.firLookupColumn = [];
    this.firLookupSysMapAcct = [];
    this.cdr.detectChanges();
  }

  // Cascade reset methods - matching original AngularJS
  firFormchanged(): void {
    this.CHECK_FIR_SCHEDULE = '';
    this.CHECK_FIR_LINE = '';
    this.CHECK_FIR_LINE_DESC = '';
    this.CHECK_FIR_COL = '';
    this.CHECK_FIR_SYS_MAP_ACCT = '';
    this.firLookupLine = [];
    this.firLookupColumn = [];
    this.firLookupSysMapAcct = [];
  }

  firSchedulechanged(): void {
    this.CHECK_FIR_LINE = '';
    this.CHECK_FIR_LINE_DESC = '';
    this.CHECK_FIR_COL = '';
    this.CHECK_FIR_SYS_MAP_ACCT = '';
    this.firLookupColumn = [];
    this.firLookupSysMapAcct = [];
  }

  firLinechanged(): void {
    this.CHECK_FIR_COL = '';
    this.CHECK_FIR_SYS_MAP_ACCT = '';
    this.firLookupSysMapAcct = [];
  }

  firColumnchanged(): void {
    this.CHECK_FIR_SYS_MAP_ACCT = '';
  }

  /** Detail columns are editable only when the row is in edit mode and tax sensitivity is Yes. */
  isFirTaxSensitiveDetailEditable(row: any): boolean {
    return !!row?.isEditable && row?.LOCAL_ACCT_TAX_SENSITIVE?.value === 'Y';
  }

  /**
   * Fired only on user change of Tax Sensitivity (native change event).
   * Original AngularJS ng-change: switching to No exits edit mode; other fields stay editable until Save.
   */
  onFirTaxSensitivityUserChange(editRow: any): void {
    if (editRow.LOCAL_ACCT_TAX_SENSITIVE?.value === 'N') {
      editRow.isEditable = false;
    }
    this.cdr.detectChanges();
  }

  // Multiplier change based on EP Type
  changeMultiplier(editRow: any): void {
    if (editRow.EP_TYPE_DETAILS?.EP_TYPE_CODE === 'AR') {
      editRow.MULTIPLIER = -1;
    } else if (editRow.EP_TYPE_DETAILS?.EP_TYPE_CODE === 'AY') {
      editRow.MULTIPLIER = 1;
    } else if (editRow.EP_TYPE_DETAILS?.EP_TYPE_CODE === 'AB') {
      editRow.MULTIPLIER = null;
    }
    this.cdr.detectChanges();
  }

  // Reset Tax Sensitive Lookup to original data
  resetFirTaxSensLookup(): void {
    if (this.fir_tax_sensitive_data_temp?.length > 0) {
      this.fir_tax_sensitive_data = this.buildFirTaxSensitiveRows(this.fir_tax_sensitive_data_temp);
    }
    this.cdr.detectChanges();
  }

  // Save - matching original AngularJS vm.save
  save(): void {
    if (this.crudLoading) {
      this._snackBar.open('Please wait while we save this request', 'Close', {
        duration: 4000,
        panelClass: ['info-snackbar']
      });
      return;
    }

    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    // Find selected system account
    const selectedIndex = this.fir_sys_acc_dropdown_data_TEMP.findIndex(
      (item: any) => item.SYS_DROPDOWN === this.fir_sys_acct_selected
    );
    
    if (selectedIndex === -1) {
      this._snackBar.open('Please select a system account', 'Close', {
        duration: 4000,
        panelClass: ['warning-snackbar']
      });
      this.isSaveClicked = false;
      this.crudLoading = false;
      this.cdr.detectChanges();
      return;
    }

    const selectedSysAcct = this.fir_sys_acc_dropdown_data_TEMP[selectedIndex];
    const firstData = this.rowData && Object.keys(this.rowData).length > 0 
      ? this.rowData 
      : this.gridData?.[0]?.data;

    // Build local_system_accounts array (matching original AngularJS structure)
    const localSystemAccounts: any[] = [];
    for (let i = 0; i < this.firlocalAccountKeys.length; i++) {
      localSystemAccounts.push({
        system_acct_key_old: firstData?.SYS_ACCT_KEY,
        system_acct_key_new: selectedSysAcct.SYS_ACCT_KEY,
        local_acct_key: this.firlocalAccountKeys[i],
        map_type: firstData?.MAP_TYPE,
        group_obj_key: firstData?.GROUP_OBJ_KEY,
        tax_year: firstData?.TAX_YEAR,
        local_chart_key: firstData?.LOCAL_CHART_KEY,
        acct_type: this.firLocalChartAcct_data.acct_type,
        scenario: this.globalService?.globalParams?.scenario
      });
    }

    // Build saveTaxSensitiveData array (matching original AngularJS structure)
    const saveTaxSensitiveData: any[] = [];
    for (const taxItem of this.fir_tax_sensitive_data) {
      if (taxItem.LOCAL_ACCT_TAX_SENSITIVE?.value === 'Y' || 
          (taxItem.LOCAL_ACCT_TAX_SENSITIVE?.value === 'N' && taxItem.EP_AUTO_KEY !== 0)) {
        saveTaxSensitiveData.push({
          NEW_LOCAL_TAX_SENSITIVITY: taxItem.LOCAL_ACCT_TAX_SENSITIVE?.value,
          SYSTEM_ACCT_KEY_OLD: firstData?.SYS_ACCT_KEY,
          POSTING_LOCAL_ACCT_KEY: taxItem.POSTING_LOCAL_ACCT_DETAILS?.POSTING_LOCAL_ACCT_KEY || null,
          LOCAL_CHART_KEY: taxItem.POSTING_LOCAL_ACCT_DETAILS?.CHART_KEY,
          TRIGGER_LOCAL_ACCT_KEY: taxItem.TRIGGER_LOCAL_ACCT_KEY,
          EP_AUTO_KEY: taxItem.EP_AUTO_KEY || 0,
          EP_DESC: taxItem.EP_DESC || '',
          PERM_TEMP_FLAG: taxItem.PERM_TEMP_FLAG?.value || null,
          EP_TYPE: taxItem.EP_TYPE_DETAILS?.EP_TYPE_CODE || null,
          MULTIPLIER: taxItem.MULTIPLIER,
          LINE_5471: taxItem.LINE_5471_DETAILS?.LINE_NO || null,
          LINE_8865: taxItem.LINE_8865_DETAILS?.LINE || null,
          DEFAULT_METHOD: taxItem.DEFAULT_METHOD?.value || null,
          PERMISSIBLE: taxItem.PERMISSIBLE?.value || null,
          AMT_SIGN: taxItem.AMT_SIGN || null,
          OFFSET_LOCAL_ACCT_KEY: taxItem.OFFSET_LOCAL_ACCT_DETAILS?.OFFSET_LOCAL_ACCT_KEY || null
        });
      }
    }

    this.firLocalSysService.saveFIRFormDetails(
      this.GENERAL_CONFIG.base_url || this.baseURLs.api,
      localSystemAccounts,
      saveTaxSensitiveData,
      this.globalService,
      this.filterParams
    ).pipe(takeUntil(this.unsubscribe$)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response && response.callSuccess === '0' && response.errorMessage && response.errorMessage !== 'null') {
          let errorMsg = response.errorMessage;
          if (errorMsg.includes('Invalid System Account Number')) {
            errorMsg += ' System Account Key: ' + selectedSysAcct.SYS_ACCT_KEY;
          }
          this._snackBar.open(errorMsg, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else if (response && response.callSuccess === '1') {
          this._snackBar.open('Data has been successfully saved', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        } else {
          this._snackBar.open('Local to System mapping has been successfully saved/updated', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  reset(): void {
    // Restore the read-only header form fields and the originally selected system account name.
    this.setupFormData();

    // Revert the System Account selection back to the originally loaded value and refresh the
    // dependent IRS form table (the lookup flow can change fir_sys_acct_selected / fir_sys_acc).
    this.restoreOriginalSystemAcct();

    // Revert all in-place edits made in the editable TaxSensitive grid by rebuilding the rows
    // from the cached original data.
    if (this.fir_tax_sensitive_data_temp?.length > 0) {
      this.fir_tax_sensitive_data = this.buildFirTaxSensitiveRows(this.fir_tax_sensitive_data_temp);
    }

    // Clear any unsaved state in the lookup section and close the lookup panel.
    this.resetFirLookup();
    this.firLookUpMapping = false;

    this.cdr.detectChanges();
  }

  /** Re-derives the originally loaded System Account selection from the cached dropdown data. */
  private restoreOriginalSystemAcct(): void {
    if (!this.fir_sys_acc_dropdown_data_TEMP?.length) {
      return;
    }
    const index = this.fir_sys_acc_dropdown_data_TEMP.findIndex(
      (item: any) => item.SYS_ACCT === this.selectedFirSysAcc
    );
    if (index !== -1) {
      this.fir_sys_acc.SYS_ACCT = this.fir_sys_acc_dropdown_data_TEMP[index];
      this.fir_sys_acct_selected = this.fir_sys_acc_dropdown_data_TEMP[index].SYS_DROPDOWN || 'Please select System Account';
      this.firLocalChartAcct_data.system_acct = this.fir_sys_acc_dropdown_data_TEMP[index].SYS_ACCT;
      this.fetchSystemAcctFormData();
    }
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}

